import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  createGame,
  PLAYER_COLORS,
  PLAYER_BORDER_COLORS,
  PLAYER_COLOR_ORDER,
  PLAYER_ON_COLOR,
} from "@/lib/api";
import SettingsPanel from "@/components/game/SettingsPanel";
import useDesignerMode from "@/hooks/useDesignerMode";
import Rulebook from "@/components/game/Rulebook";
import { ArrowLeft, BookOpen, Check, Settings } from "lucide-react";

function ColorSwatch({ color, selected, disabled, onClick }) {
  const bg = PLAYER_COLORS[color];
  const border = PLAYER_BORDER_COLORS[color];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`color-swatch-${color}${selected ? "-selected" : ""}`}
      className="relative h-11 w-11 disabled:opacity-30 rounded-md"
      aria-pressed={selected}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        boxShadow: selected ? "0 0 0 2px var(--card), 0 0 0 3.5px var(--sea)" : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "box-shadow 150ms ease",
      }}
      aria-label={`color ${color}`}
    >
      {selected && (
        <span className="absolute inset-0 flex items-center justify-center" style={{ color: PLAYER_ON_COLOR[color] }}>
          <Check size={18} strokeWidth={2.2} />
        </span>
      )}
    </button>
  );
}

export default function SetupScreen() {
  const navigate = useNavigate();
  const [playerCount, setPlayerCount] = useState(4);
  const [players, setPlayers] = useState([
    { name: "Player 1", color: "red" },
    { name: "Player 2", color: "yellow" },
    { name: "Player 3", color: "purple" },
    { name: "Player 4", color: "orange" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const designer = useDesignerMode();
  const [showRulebook, setShowRulebook] = useState(false);
  const closeRulebook = useCallback(() => setShowRulebook(false), []);

  const activePlayers = players.slice(0, playerCount);
  const usedColors = new Set(activePlayers.map((p) => p.color));

  const updateName = (i, name) =>
    setPlayers((prev) => prev.map((p, idx) => (idx === i ? { ...p, name } : p)));

  const pickColor = (i, color) => {
    if (usedColors.has(color) && players[i].color !== color) return;
    setPlayers((prev) => prev.map((p, idx) => (idx === i ? { ...p, color } : p)));
  };

  const handleStart = async () => {
    if (activePlayers.some((p) => !p.name.trim())) {
      toast.error("Every player needs a name");
      return;
    }
    const colors = activePlayers.map((p) => p.color);
    if (new Set(colors).size !== colors.length) {
      toast.error("Each player must pick a unique color");
      return;
    }
    try {
      setSubmitting(true);
      const game = await createGame(activePlayers);
      navigate(`/game/${game.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start game");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="setup-screen"
      className="h-screen overflow-y-auto w-full flex justify-center px-4 py-8"
    >
      <div className="w-full max-w-2xl my-auto">
        <Link to="/" className="btn-ghost -ml-3" data-testid="setup-back-link">
          <ArrowLeft size={16} strokeWidth={1.8} /> Home
        </Link>
        <div className="mt-4 mb-6">
          <div className="label-kicker text-[color:var(--ink-3)]" data-testid="setup-kicker">
            A board game for 2–4 players
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight text-[color:var(--ink)] mt-2">
            Porosity
          </h1>
          <p className="mt-2 text-[color:var(--ink-3)] max-w-md leading-relaxed">
            Build a shared city on a shifting shoreline. Pick 2, 3 or 4 players
            and claim a colour. Everyone places 2 starting House Cubes, then
            turns go in reverse seat order.
          </p>
        </div>

        <div className="bg-[color:var(--card)] rounded-[10px] shadow-[var(--shadow-card)] p-7">
          <div className="flex items-center justify-between mb-6">
            <div className="label-kicker text-[color:var(--ink-3)]">How many players</div>
            <div className="flex gap-3" data-testid="player-count-toggle">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  data-testid={`player-count-${n}`}
                  onClick={() => setPlayerCount(n)}
                  aria-pressed={playerCount === n}
                  className="btn-tactile"
                  style={playerCount === n ? { borderColor: "var(--sea)", color: "var(--sea)", boxShadow: "inset 0 0 0 1px var(--sea)" } : undefined}
                >
                  {n} players
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {activePlayers.map((p, i) => (
              <div
                key={i}
                data-testid={`player-row-${i}`}
                className="flex items-center gap-4 p-3 border border-[color:var(--line)] bg-[color:var(--card)]"
              >
                <div
                  className="h-10 w-10 shrink-0 rounded-md flex items-center justify-center"
                  style={{
                    background: PLAYER_COLORS[p.color],
                    border: `2px solid ${PLAYER_BORDER_COLORS[p.color]}`,
                    boxShadow: "var(--shadow-card)",
                    color: "#ffffff",
                    fontFamily: "Figtree, sans-serif",
                    fontWeight: 800,
                    fontSize: 18,
                  }}
                >
                  {i + 1}
                </div>
                <Input
                  data-testid={`player-name-${i}`}
                  value={p.name}
                  onChange={(e) => updateName(i, e.target.value)}
                  placeholder={`Player ${i + 1} name`}
                  className="rounded-[8px] border border-[color:var(--line)] bg-[color:var(--card)] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#DDA15E]"
                  maxLength={24}
                />
                <div className="flex gap-2">
                  {PLAYER_COLOR_ORDER.map((c) => (
                    <ColorSwatch
                      key={c}
                      color={c}
                      selected={p.color === c}
                      disabled={usedColors.has(c) && p.color !== c}
                      onClick={() => pickColor(i, c)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-[color:var(--ink-3)]">
              Board size:{" "}
              <span className="font-bold text-[color:var(--ink)]" data-testid="board-size-preview">
                {playerCount === 4 ? "6 × 7" : playerCount === 3 ? "5 × 6" : "5 × 5"}
              </span>
              <span className="ml-2 text-xs text-[color:var(--ink-3)]">
                (columns × rows, sea along the bottom)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="home-rulebook-btn"
                onClick={() => setShowRulebook(true)}
                className="btn-ghost"
              >
                <BookOpen size={16} strokeWidth={1.8} /> Rulebook
              </button>
              <button
                data-testid="start-game-btn"
                disabled={submitting}
                onClick={handleStart}
                className="btn-tactile btn-tactile--primary"
              >
                {submitting ? "Starting..." : "Start game →"}
              </button>
            </div>
          </div>
        </div>

        {designer && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              data-testid="open-settings-btn"
              onClick={() => setShowSettings(true)}
              className="btn-ghost"
            >
              <Settings size={16} strokeWidth={1.8} /> House Rules
            </button>
          </div>
        )}

      </div>

      {showSettings && designer && (
        <SettingsPanel
          gameId={null}
          onClose={() => setShowSettings(false)}
        />
      )}
      <Rulebook open={showRulebook} onClose={closeRulebook} />
    </div>
  );
}
